import { FormEvent, useEffect, useMemo, useState } from "react";
import { api } from "../api/client";
import type {
  Block,
  Driver,
  DriverDuty,
  ListResponse,
  Vehicle,
  VehicleAssignment,
} from "../api/types";

function unwrap<T>(value: ListResponse<T> | T[] | undefined | null): T[] {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (Array.isArray((value as ListResponse<T>).data)) {
    return (value as ListResponse<T>).data;
  }
  return [];
}

/** Real block_id values are generator-assigned ("sched-<hash>-shift-003");
 *  this just reformats that real string for a readable dropdown/list label,
 *  it doesn't invent a code. */
function blockLabel(blockId: string): string {
  const m = blockId.match(/shift-(\d+)$/i);
  if (m) return `Shift ${Number(m[1])}`;
  return blockId.length > 20 ? blockId.slice(0, 10) + "…" : blockId;
}

/** Real employee_id values in this fleet are login identifiers
 *  ("ORG7517@ORANGE.METRO"); show just the local part for a compact label. */
function employeeCode(employeeId: string): string {
  return employeeId.split("@")[0] || employeeId;
}

function enumLabel(v?: string): string {
  if (!v) return "—";
  const s = v.toLowerCase().replace(/_/g, " ");
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export default function ReassignPage() {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [vAssignments, setVAssignments] = useState<VehicleAssignment[]>([]);
  const [duties, setDuties] = useState<DriverDuty[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const [vehicleId, setVehicleId] = useState("");
  const [blockId, setBlockId] = useState("");
  const [serviceDate, setServiceDate] = useState(() =>
    new Date().toISOString().slice(0, 10)
  );

  const [driverId, setDriverId] = useState("");
  const [dutyBlockId, setDutyBlockId] = useState("");
  const [scheduledStart, setScheduledStart] = useState("");
  const [scheduledEnd, setScheduledEnd] = useState("");

  async function loadAll() {
    try {
      const [v, d, b, va, du] = await Promise.all([
        api.listVehicles().catch(() => null),
        api.listDrivers().catch(() => null),
        api.listBlocks({ date: serviceDate }).catch(() => null),
        api.listVehicleAssignments({ date: serviceDate }).catch(() => null),
        api.listDriverDuties({ date: serviceDate }).catch(() => null),
      ]);
      setVehicles(unwrap(v));
      setDrivers(unwrap(d));
      setBlocks(unwrap(b));
      setVAssignments(unwrap(va));
      setDuties(unwrap(du));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load");
    }
  }

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceDate]);

  async function onAssignVehicle(e: FormEvent) {
    e.preventDefault();
    if (!vehicleId || !blockId) { setError("Pick a vehicle and a block"); return; }
    setPending(true);
    try {
      await api.createVehicleAssignment({
        vehicle_id: vehicleId,
        block_id: blockId,
        service_date: serviceDate,
        status: "scheduled",
      });
      setError(null);
      await loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Assign failed");
    } finally {
      setPending(false);
    }
  }

  async function onAssignDriver(e: FormEvent) {
    e.preventDefault();
    if (!driverId || !scheduledStart || !scheduledEnd) { setError("Pick a driver and a start/end time"); return; }
    setPending(true);
    try {
      await api.createDriverDuty({
        driver_id: driverId,
        block_id: dutyBlockId || undefined,
        service_date: serviceDate,
        duty_type: "regular",
        scheduled_start: scheduledStart,
        scheduled_end: scheduledEnd,
        status: "scheduled",
      });
      setError(null);
      await loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Duty assign failed");
    } finally {
      setPending(false);
    }
  }

  const blockLabelById = useMemo(() => {
    const m: Record<string, string> = {};
    for (const b of blocks) m[b.id] = blockLabel(b.block_id);
    return m;
  }, [blocks]);

  const driverNameById = useMemo(() => {
    const m: Record<string, string> = {};
    for (const d of drivers) m[d.id] = `${d.first_name} ${d.last_name}`.trim();
    return m;
  }, [drivers]);

  // Real driver <- block pairing for the selected service date, so the
  // vehicle roster can show who's driving without fabricating a link.
  const driverNameByBlock = useMemo(() => {
    const m: Record<string, string> = {};
    for (const d of duties) {
      if (d.block_id && d.status !== "cancelled") m[d.block_id] = driverNameById[d.driver_id] ?? "";
    }
    return m;
  }, [duties, driverNameById]);

  const assignmentByVehicle = useMemo(() => {
    const m: Record<string, VehicleAssignment> = {};
    for (const a of vAssignments) {
      if (a.status !== "cancelled") m[a.vehicle_id] = a;
    }
    return m;
  }, [vAssignments]);

  function vehicleDetail(v: Vehicle): { pill: string; tone: "ok" | "info" | "warn"; detail: string } {
    if ((v.status || "").toLowerCase() !== "active") {
      return { pill: enumLabel(v.status), tone: "warn", detail: `Workshop · ${enumLabel(v.status)}` };
    }
    const a = assignmentByVehicle[v.id];
    if (a) {
      const label = blockLabelById[a.block_id] ?? a.block_id.slice(0, 8) + "…";
      const driverName = driverNameByBlock[a.block_id];
      return { pill: "In service", tone: "ok", detail: `On block ${label}${driverName ? " · " + driverName : ""}` };
    }
    return { pill: "Available", tone: "info", detail: "At depot · unassigned" };
  }

  const dutiesByDriver = useMemo(() => {
    const m: Record<string, DriverDuty[]> = {};
    for (const d of duties) {
      if (d.status === "cancelled") continue;
      (m[d.driver_id] ??= []).push(d);
    }
    return m;
  }, [duties]);

  function driverDetail(d: Driver): { pill: string; tone: "ok" | "info" | "muted" } {
    const todays = dutiesByDriver[d.id] ?? [];
    if (todays.length === 0) return { pill: "Off duty", tone: "muted" };
    const now = Date.now();
    const active = todays.find((x) => {
      if (x.status === "active") return true;
      const s = new Date(x.scheduled_start).getTime();
      const e = new Date(x.scheduled_end).getTime();
      return now >= s && now <= e;
    });
    return active ? { pill: "On duty", tone: "ok" } : { pill: "Standby", tone: "info" };
  }

  return (
    <div className="page reassign-page">
      <div className="reassign-header">
        <h1>Reassignment</h1>
        <p className="secondary-text">Move vehicles between blocks and assign driver duties.</p>
      </div>
      {error && <div className="banner banner-error">{error}</div>}

      <div className="reassign-hero-grid">
        <section className="card reassign-compose-card">
          <h3>Assign vehicle to block</h3>
          <form onSubmit={onAssignVehicle}>
            <label className="reassign-field">
              <span>Vehicle</span>
              <select value={vehicleId} onChange={(e) => setVehicleId(e.target.value)} required>
                <option value="">— pick a vehicle —</option>
                {vehicles.map((v) => {
                  const { detail } = vehicleDetail(v);
                  return <option key={v.id} value={v.id}>{v.vehicle_id} · {detail}</option>;
                })}
              </select>
            </label>
            <div className="reassign-form-row">
              <label className="reassign-field">
                <span>Block</span>
                <select value={blockId} onChange={(e) => setBlockId(e.target.value)} required>
                  <option value="">— pick a block —</option>
                  {blocks.map((b) => (
                    <option key={b.id} value={b.id}>{blockLabel(b.block_id)}</option>
                  ))}
                </select>
              </label>
              <label className="reassign-field">
                <span>Service date</span>
                <input
                  type="date"
                  value={serviceDate}
                  onChange={(e) => setServiceDate(e.target.value)}
                  required
                />
              </label>
            </div>
            <button className="btn primary reassign-submit-btn" type="submit" disabled={pending}>
              {pending ? "Assigning…" : "Assign vehicle"}
            </button>
          </form>
        </section>

        <section className="card reassign-compose-card">
          <h3>Assign driver duty</h3>
          <form onSubmit={onAssignDriver}>
            <label className="reassign-field">
              <span>Driver</span>
              <select value={driverId} onChange={(e) => setDriverId(e.target.value)} required>
                <option value="">— pick a driver —</option>
                {drivers.map((d) => (
                  <option key={d.id} value={d.id}>{d.first_name} {d.last_name} · {employeeCode(d.employee_id)}</option>
                ))}
              </select>
            </label>
            <div className="reassign-form-row">
              <label className="reassign-field">
                <span>Scheduled start</span>
                <input
                  type="datetime-local"
                  value={scheduledStart}
                  onChange={(e) => setScheduledStart(e.target.value)}
                  required
                />
              </label>
              <label className="reassign-field">
                <span>Scheduled end</span>
                <input
                  type="datetime-local"
                  value={scheduledEnd}
                  onChange={(e) => setScheduledEnd(e.target.value)}
                  required
                />
              </label>
            </div>
            <details className="reassign-advanced">
              <summary>Advanced</summary>
              <label className="reassign-field" style={{ marginTop: 8 }}>
                <span>Block (optional)</span>
                <select value={dutyBlockId} onChange={(e) => setDutyBlockId(e.target.value)}>
                  <option value="">— none —</option>
                  {blocks.map((b) => (
                    <option key={b.id} value={b.id}>{blockLabel(b.block_id)}</option>
                  ))}
                </select>
              </label>
            </details>
            <button className="btn primary reassign-submit-btn" type="submit" disabled={pending}>
              {pending ? "Assigning…" : "Assign duty"}
            </button>
          </form>
        </section>
      </div>

      <div className="reassign-hero-grid">
        <section className="card reassign-roster-card">
          <div className="reassign-roster-head">
            <h3><span className="reassign-bar" />Vehicles <span className="secondary-text">{vehicles.length} in fleet</span></h3>
          </div>
          <div className="reassign-roster-list">
            {vehicles.length === 0 && <div className="empty">— none —</div>}
            {vehicles.map((v) => {
              const { pill, tone, detail } = vehicleDetail(v);
              return (
                <div className="reassign-roster-row" key={v.id}>
                  <div className="reassign-roster-main">
                    <div className="reassign-roster-title mono">{v.vehicle_id}</div>
                    <div className="reassign-roster-sub">{detail}</div>
                  </div>
                  <span className={`reassign-pill ${tone}`}>{pill}</span>
                </div>
              );
            })}
          </div>
        </section>

        <section className="card reassign-roster-card">
          <div className="reassign-roster-head">
            <h3><span className="reassign-bar" />Drivers <span className="secondary-text">{drivers.length} rostered</span></h3>
          </div>
          <div className="reassign-roster-list">
            {drivers.length === 0 && <div className="empty">— none —</div>}
            {drivers.map((d) => {
              const { pill, tone } = driverDetail(d);
              const name = `${d.first_name} ${d.last_name}`.trim();
              const initials = (name.split(/\s+/).filter(Boolean).map((w) => w[0]).join("") || "?").slice(0, 2).toUpperCase();
              return (
                <div className="reassign-roster-row" key={d.id}>
                  <span className="reassign-avatar">{initials}</span>
                  <div className="reassign-roster-main">
                    <div className="reassign-roster-title">{name}</div>
                    <div className="reassign-roster-sub mono">{employeeCode(d.employee_id)}</div>
                  </div>
                  <span className={`reassign-pill ${tone}`}>{pill}</span>
                </div>
              );
            })}
          </div>
        </section>
      </div>

      <div className="reassign-hero-grid">
        <section className="card">
          <h3>Vehicle assignments ({vAssignments.length})</h3>
          <table className="data-table compact">
            <thead>
              <tr><th>Date</th><th>Vehicle</th><th>Block</th><th>Status</th></tr>
            </thead>
            <tbody>
              {vAssignments.length === 0 && (
                <tr><td colSpan={4} className="empty">— none —</td></tr>
              )}
              {vAssignments.map((a) => (
                <tr key={a.id}>
                  <td>{a.service_date.slice(0, 10)}</td>
                  <td>{vehicles.find((v) => v.id === a.vehicle_id)?.vehicle_id ?? a.vehicle_id.slice(0, 8) + "…"}</td>
                  <td>{blockLabelById[a.block_id] ?? a.block_id.slice(0, 8) + "…"}</td>
                  <td>{enumLabel(a.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
        <section className="card">
          <h3>Driver duties ({duties.length})</h3>
          <table className="data-table compact">
            <thead>
              <tr><th>Date</th><th>Driver</th><th>Block</th><th>Status</th></tr>
            </thead>
            <tbody>
              {duties.length === 0 && (
                <tr><td colSpan={4} className="empty">— none —</td></tr>
              )}
              {duties.map((d) => (
                <tr key={d.id}>
                  <td>{d.service_date.slice(0, 10)}</td>
                  <td>{driverNameById[d.driver_id] ?? d.driver_id.slice(0, 8) + "…"}</td>
                  <td>{d.block_id ? (blockLabelById[d.block_id] ?? d.block_id.slice(0, 8) + "…") : "—"}</td>
                  <td>{enumLabel(d.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>
    </div>
  );
}
